import { money } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useFunder } from "@/funder/FunderContext";

/** Normalize the live draft breakdown (engine) into the display shape. */
export function fromDraft(b) {
  const sum = (k) => (b.rounds || []).reduce((s, r) => s + (r[k] || 0), 0);
  return {
    hours: b.hours_shown,
    charges: b.charges_to_date,
    l1: sum("l1"), l2: sum("l2"), l3: sum("l3"),
    acsa_coverage: b.acsa_coverage,
    client_responsibility: b.responsibility_to_date,
    payments: b.payments_received_to_date,
    amount_due: b.amount_due,
  };
}

/** Normalize a frozen invoice snapshot into the same display shape. */
export function fromSnapshot(s) {
  return {
    hours: s.hours_shown,
    charges: s.charges,
    l1: s.l1, l2: s.l2, l3: s.l3,
    acsa_coverage: (s.l1 || 0) + (s.l2 || 0) + (s.l3 || 0),
    client_responsibility: s.responsibility_to_date,
    payments: s.payments_at_issue,
    amount_due: s.amount_due,
  };
}

function Line({ label, value, sign, tone, strong }) {
  return (
    <div className={cn("flex items-center justify-between py-1.5", strong && "border-t border-border pt-2")}>
      <span className={cn("text-sm", strong ? "font-medium text-foreground" : "text-muted-foreground")}>{label}</span>
      <span className={cn("nums text-sm", tone === "acsa" ? "text-status-paid" : tone === "client" ? "text-primary" : "text-foreground", strong && "font-semibold")}>
        {sign === "-" ? "−" : ""}{money(value)}
      </span>
    </div>
  );
}

/** To-date breakdown for a payer, shared by the review dialog and the snapshot detail. */
export function InvoiceBreakdown({ payer, data }) {
  const { abbr } = useFunder();
  const isAcsa = payer === "ACSA";
  return (
    <div data-testid="invoice-breakdown">
      {isAcsa ? (
        <>
          <Line label="Charges to date" value={data.charges} />
          <Line label="Level 1 credit" value={data.l1} sign="-" />
          <Line label="Level 2 credit" value={data.l2} sign="-" />
          <Line label="Level 3 credit" value={data.l3} sign="-" />
          <Line label={`Total ${abbr} coverage to date`} value={data.acsa_coverage} tone="acsa" />
          <Line label={`Less prior ${abbr} paid`} value={data.payments} sign="-" />
          <Line label="Amount due" value={data.amount_due} strong tone="acsa" />
        </>
      ) : (
        <>
          <Line label={`Services to date (${Number(data.hours || 0).toFixed(2)} h)`} value={data.charges} />
          <Line label={`Less ${abbr} coverage to date`} value={data.acsa_coverage} sign="-" tone="acsa" />
          <Line label="Client responsibility to date" value={data.client_responsibility} tone="client" />
          <Line label="Less payments received" value={data.payments} sign="-" />
          <Line label="Amount due" value={data.amount_due} strong tone="client" />
        </>
      )}
    </div>
  );
}
